import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { RoutePath } from "../../constants/RoutePath";
import { useBasePage } from "../../hooks/useBasePage";
import { DefaultTenantTheme } from "../../theme/PantryStackTheme";
import { useTenant } from "../../tenant/useTenant";
import type { TenantThemeSettings } from "../../models/TenantThemeSettings";
import { Tenant } from "../../models/Tenant";
import { DataState } from "../../models/DataState";
import { hash } from "../../utils/Util";

export function TenantSetup() {
  const navigate = useNavigate();
  const { TenantUrl, PreviewTheme, SaveTenant } = useTenant();
  const { state, setStateProperties, handleError } = useBasePage<Tenant>({
    DataState: DataState.New,
    BusinessName: "",
    Addresses: [""],
    FirstName: "",
    LastName: "",
    Email: "",
    Password: "",
    Theme: DefaultTenantTheme,
    TenantUrl: "",
    BusinessPhones: "",
    DefaultWareHouseName: "",
  });

  const SetProperty = <K extends keyof Tenant>(
    property: K,
    value: Tenant[K],
  ): void => {
    setStateProperties({ [property]: value });
  };

  const SetThemeProperty = <K extends keyof TenantThemeSettings>(
    property: K,
    value: TenantThemeSettings[K],
  ): void => {
    const Theme = { ...state.Theme, [property]: value };
    SetProperty("Theme", Theme);
    PreviewTheme(Theme);
  };

  const SetAddress = (index: number, value: string): void => {
    const Addresses = [...state.Addresses];
    Addresses[index] = value;
    SetProperty("Addresses", Addresses);
  };

  const AddAddress = (): void => {
    SetProperty("Addresses", [...state.Addresses, ""]);
  };

  const RemoveAddress = (index: number): void => {
    if (state.Addresses.length === 1) {
      return;
    }

    SetProperty(
      "Addresses",
      state.Addresses.filter((_, addressIndex) => addressIndex !== index),
    );
  };

  const SetLogo = (files: File[]): void => {
    const LogoFile = files[0];
    SetProperty("LogoFile", LogoFile);
    // SetProperty("LogoUrl", Logo ? URL.createObjectURL(Logo) : undefined);
  };

  const Submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();

    const Addresses = state.Addresses.map((address) => address.trim()).filter(
      Boolean,
    );
    // const phones = state.BusinessPhones.map((address) => address.trim()).filter(
    //   Boolean,
    // );

    if (Addresses.length === 0) {
      handleError(new Error("At least one business address is required."));
      return;
    }

    if (!state.LogoFile) {
      handleError(new Error("A business logo is required."));
      return;
    }

    try {
      var psw = await hash(state.Password as string);
      await SaveTenant({
        BusinessName: state.BusinessName.trim(),
        Addresses,
        TenantUrl,
        Theme: state.Theme,
        FirstName: state?.FirstName?.trim(),
        LastName: state?.LastName?.trim(),
        Email: state?.Email?.trim(),
        Password: psw,
        LogoFile: state.LogoFile,
        BusinessPhones: state.BusinessPhones,
        DefaultWareHouseName: state.DefaultWareHouseName?.trim() || undefined,
        DataState: DataState.New,
      });

      navigate(RoutePath.Login, { replace: true });
    } catch {
      // ApiInterceptor displays API errors through the central ErrorProvider.
    }
  };

  return {
    state,
    TenantUrl,
    SetProperty,
    SetThemeProperty,
    SetAddress,
    AddAddress,
    RemoveAddress,
    SetLogo,
    Submit,
  };
}
